import { LuArrowLeft, LuCheck, LuGift, LuMedal, LuSparkles, LuStar, LuTarget, LuX } from "react-icons/lu";
import { HowTo } from "../components/HowTo";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, get, post } from "../api";
import { Avatar } from "../components/Avatar";
import { GoalStrip, PodiumStage } from "../components/heroes";
import { StreakNote } from "../components/streak";
import { MOTTO, Plate, PlayerCard, subtitleFor, useScene } from "../components/Arena";
import { FeedbackBox, QuestionCard, revealFor, type FB, type QView } from "../components/QuestionCard";
import { Count, ErrorBox, GAME_META, Modal, Spinner, Coin } from "../components/ui";
import { Confetti, coinFlight } from "../components/fx";
import { FxLayer, SoundToggle, banner, useOnChange } from "../components/juice";
import { buzz, initSound, play } from "../sound";
import { GameView } from "../games";
import { Link, navigate } from "../router";
import { useApp, useLoad } from "../store";
import { NextCard, pickNext } from "../components/goals";
import { TOWERS, TOWER_TYPES } from "../../shared/games/taarnTowers";

export interface GameProps {
  view: any;
  act: (body: any) => Promise<boolean>;
  answer: (given: number | string, extra?: Record<string, unknown>) => Promise<void>;
  busy: boolean;
  feedback: FB | null;
  clearFeedback: () => void;
}

/**
 * The sounds and banners that belong to the match as a whole (the games add their own for what happens on the board):
 * "Din tur!" when the turn comes back to you, and a banner when your streak of right answers reaches 3, 5, 8 …
 */
function useMatchCues(view: any) {
  const st = view?.state;
  const myTurn = !!st && st.kind === "turn" && st.phase !== "over" && st.turn === st.you;
  const streak: number = (st?.stats?.[st?.you]?.streak ?? st?.streak ?? 0) as number;
  const key = view?.id ?? "";
  useOnChange(myTurn, (now) => { if (now) { play("turn"); banner("Din tur!", "blue"); } }, key);
  useOnChange(streak, (n, before) => {
    if (n > before && [3, 5, 8, 10, 15, 20, 30].includes(n)) { banner(`${n} i træk!`, "gold"); play("streak"); buzz([20, 40, 20]); }
  }, key);
}

export function MatchPage({ id }: { id: string }) {
  const { toast, reload } = useApp();
  const [view, setView] = useState<any>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<FB | null>(null);
  const [leaving, setLeaving] = useState(false);
  const statusRef = useRef("");
  useScene(view?.game);
  const inflight = useRef(false);
  useMatchCues(view);
  const hasView = !!view, inClass = view?.mode === "class";
  useEffect(() => { if (hasView) initSound(inClass); }, [hasView, inClass]);

  const refresh = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const v = await get(`/api/matches/${id}`);
      setView(v);
      setOffline(false);
      setError(null);
      if (statusRef.current !== v.status && (v.status === "finished" || v.status === "aborted")) void reload();
      statusRef.current = v.status;
    } catch (e) {
      if (e instanceof ApiError && e.status === 0) setOffline(true);
      else setError(e instanceof ApiError ? e : new ApiError(0, "Noget gik galt."));
    } finally {
      inflight.current = false;
    }
  }, [id, reload]);

  useEffect(() => {
    void refresh();
    let es: EventSource | null = null;
    try {
      es = new EventSource(`/api/matches/${id}/stream`);
      es.addEventListener("v", () => void refresh());
    } catch {
      /* polling covers it */
    }
    const iv = setInterval(() => {
      if (statusRef.current === "finished" || statusRef.current === "aborted") return;
      void refresh();
    }, 1800);
    return () => {
      es?.close();
      clearInterval(iv);
    };
  }, [id, refresh]);

  const act = useCallback(
    async (body: any) => {
      setBusy(true);
      try {
        await post(`/api/matches/${id}/act`, body);
        await refresh();
        return true;
      } catch (e) {
        toast(e instanceof ApiError ? e.message : "Noget gik galt.", "error");
        await refresh();
        return false;
      } finally {
        setBusy(false);
      }
    },
    [id, refresh, toast],
  );

  const answer = useCallback(
    async (given: number | string, extra?: Record<string, unknown>) => {
      const pid = view?.state?.question?.pid;
      if (!pid) return;
      setBusy(true);
      try {
        const r = await post<{ feedback?: FB; locked?: boolean }>(`/api/matches/${id}/answer`, { pid, given, ...(extra ?? {}) });
        if (r.feedback) {
          setFeedback(r.feedback);
          play(r.feedback.correct ? "correct" : "wrong");
          buzz(r.feedback.correct ? 18 : [40, 30, 40]);
        }
        await refresh();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : "Noget gik galt.", "error");
        await refresh();
      } finally {
        setBusy(false);
      }
    },
    [id, view, refresh, toast],
  );

  if (error && !view) {
    return (
      <div className="stack">
        <ErrorBox error={error} retry={refresh} />
        <Link to="/spil" className="btn ghost">Til spillene</Link>
      </div>
    );
  }
  if (!view) return <Spinner label="Henter spillet…" />;

  const m = GAME_META[view.game]!;
  const leave = async () => {
    try {
      await post(`/api/matches/${id}/leave`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Kunne ikke forlade spillet.", "error");
    }
    await reload();
    navigate(view.mode === "class" ? "/klasse" : "/spil");
  };
  const done = view.status === "finished" || view.status === "aborted";

  const mine = view.players.find((p: any) => p.seat === view.you);
  const others = view.players.filter((p: any) => p.seat !== view.you);
  const st = view.state;
  const sub = st?.round ? `Runde ${Math.min(st.round.idx + 1, st.round.total)}/${st.round.total}` : subtitleFor(view);
  return (
    <div className="arena">
      <FxLayer />
      {offline && <div className="notice warn" role="status">Forbindelsen blev afbrudt. Vi prøver igen automatisk… Dit spil er gemt på serveren.</div>}
      <div className="row between">
        {!done && view.mode !== "class" ? <button className="btn ghost small" onClick={() => setLeaving(true)}><LuArrowLeft size={16} aria-hidden="true" /> Forlad kamp</button> : <span />}
        <div className="pills">
          {view.game !== "taarn" && view.game !== "monopoly" && <SoundToggle />}
          {view.config.practice && <span className="pill">Øvetilstand</span>}
          <span className="pill">{view.config.grade}. klasse</span>
        </div>
      </div>
      {/* EduPoly shows its players on the board itself */}
      {!(view.game === "monopoly" && view.status !== "lobby") && (
        <div className="arena-top">
          {mine ? <PlayerCard p={mine} view={view} side="left" /> : <span />}
          <Plate title={m.name} sub={sub} />
          <div className="stack" style={{ gap: 8 }}>
            {others.slice(0, 3).map((p: any) => <PlayerCard key={p.seat} p={p} view={view} side="right" />)}
          </div>
        </div>
      )}
      {view.status === "paused" && <div className="notice warn" role="status">Din lærer har sat spillet på pause.</div>}
      {view.status === "lobby" ? (
        <Lobby view={view} onChange={refresh} onLeave={leave} />
      ) : (
        <>
          {view.state && <GameView view={view} act={act} answer={answer} busy={busy || view.status === "paused"} feedback={feedback} clearFeedback={() => setFeedback(null)} />}
          {done && <Results view={view} />}
        </>
      )}
      {leaving && (
        <Modal title="Forlad spillet?" onClose={() => setLeaving(false)}>
          <p>{view.status === "lobby" ? "Du forlader rummet." : view.mode === "online" ? "Hvis du forlader et online-spil, tæller det som et nederlag (hvis spillet er kommet i gang)." : "Hvis du forlader nu, afsluttes spillet. Dine rigtige svar giver stadig mønter og XP, men ikke sejrs- eller gennemførelsesbonus."}</p>
          <div className="row">
            <button className="btn danger" onClick={leave}>Ja, forlad</button>
            <button className="btn ghost" onClick={() => setLeaving(false)}>Bliv i spillet</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Lobby({ view, onChange, onLeave }: { view: any; onChange: () => void; onLeave: () => void }) {
  const { toast } = useApp();
  const [waited, setWaited] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWaited((w) => w + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const live = view.players.filter((p: any) => !p.left);
  const alone = live.length === 1;
  const enough = live.length >= view.rules.min;
  const everyoneReady = live.every((p: any) => p.ready);
  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Noget gik galt.", "error");
    }
    onChange();
  };
  const me = live.find((p: any) => p.seat === view.you);
  return (
    <div className="card stack">
      <h2>Venteværelse</h2>
      {view.code && (
        <div className="notice">
          Giv din ven denne kode (eller send et link): <strong style={{ fontSize: "1.6rem", letterSpacing: 4 }} aria-label={`Rumkode ${view.code.split("").join(" ")}`}>{view.code}</strong>
          <button className="btn small ghost" style={{ marginLeft: 12 }} onClick={() => navigator.clipboard?.writeText(view.code).then(() => toast("Koden er kopieret", "ok"))}>Kopiér</button>
        </div>
      )}
      <div className="stack">
        {live.map((p: any) => (
          <div key={p.seat} className="row between">
            <div className="row"><Avatar equipped={p.avatar} size={40} label="" /><strong>{p.username}</strong>{p.host && <span className="badge">Vært</span>}</div>
            <span className={p.ready ? "okmsg" : "muted"}>{p.ready ? "✓ Klar" : "Venter…"}</span>
          </div>
        ))}
        {alone && <p className="muted" role="status">Leder efter modspillere… ({waited} s) – spillere skal bruge samme indstillinger for at blive matchet.</p>}
      </div>
      <div className="row">
        {!view.isHost && me && <button className="btn lime" onClick={() => run(() => post(`/api/matches/${view.id}/ready`, { ready: !me.ready }))}>{me.ready ? "Jeg er ikke klar" : "Jeg er klar!"}</button>}
        {view.isHost && <button className="btn lime" disabled={!enough || !everyoneReady} onClick={() => run(() => post(`/api/matches/${view.id}/start`))}>Start spillet</button>}
        {view.isHost && alone && view.rules.bot && (
          <button className="btn violet" onClick={() => run(() => post(`/api/matches/${view.id}/bot`, { level: view.config.botLevel ?? "medium" }))}>
            {waited > 15 ? "Ingen fundet – spil mod en bot" : "Spil mod en bot i stedet"}
          </button>
        )}
        <button className="btn ghost" onClick={onLeave}>Forlad</button>
      </div>
      <small className="muted">Botten er altid tydeligt markeret som computer. Online-spil kan spilles med 2 spillere ad gangen.</small>
    </div>
  );
}

/** the one sub-goal that is closest to done – a reason to press "Spil igen" */
function NextGoal() {
  const ach = useLoad(() => get<{ groups: any[] }>("/api/achievements"), []);
  const n = ach.data ? pickNext(ach.data.groups, 1)[0] : null;
  if (!n) return null;
  return <div className="stack"><h3 className="sec-sub">Næste mål</h3><NextCard def={n.def} hue={n.hue} compact /></div>;
}

const OUTCOME_TITLE: Record<string, string> = { win: "Sejr!", loss: "Godt kæmpet!", draw: "Uafgjort", none: "Spillet er slut" };

function Results({ view }: { view: any }) {
  const { me } = useApp();
  const res = view.result;
  const st = view.state;
  const you = res?.you;
  const outcome: string | undefined = you?.outcome, leveled = !!you?.levelUp;
  const rewardsRef = useRef<HTMLDivElement>(null);
  const earned: number = you?.coins ?? 0;
  useEffect(() => {
    if (!outcome) return;
    play(outcome === "win" ? "win" : outcome === "draw" ? "draw" : "lose");
    buzz(outcome === "win" ? [30, 50, 30, 50, 80] : 25);
    const t1 = setTimeout(() => play("coin"), outcome === "win" ? 900 : 600);
    const t2 = leveled ? setTimeout(() => play("levelup"), 1700) : undefined;
    /* the earned coins fly up to the counter in the top bar */
    const t3 = earned > 0 ? setTimeout(() => coinFlight(rewardsRef.current?.querySelector<HTMLElement>(".reward") ?? rewardsRef.current, Math.ceil(earned / 6)), 1100) : undefined;
    return () => { clearTimeout(t1); if (t2) clearTimeout(t2); if (t3) clearTimeout(t3); };
  }, [view.id, outcome, leveled, earned]);
  if (!res) return null;
  if (res.aborted || !you) {
    return (
      <div className="card big-result">
        <h2>Spillet blev afbrudt</h2>
        <p className="muted">Der var for få svar til, at spillet tæller – derfor er der hverken mønter, XP eller nederlag.</p>
        <Link to="/spil" className="btn">Til spillene</Link>
      </div>
    );
  }
  const review: any[] = st?.review ?? [];
  return (
    <div className="card stack" aria-live="polite">
      <div className="victory" style={you.outcome === "win" ? undefined : { minHeight: 150 }}>
        {you.outcome === "win" && <img src="/art/hero-sejr.jpg" alt="" />}
        {you.outcome === "win" && <Confetti n={60} ms={4200} />}
        <div className="vtitle">
          <h2 className={`title-game ${you.outcome === "win" ? "gold" : ""}`}>{OUTCOME_TITLE[you.outcome].toUpperCase()}</h2>
          {you.rank && <p style={{ margin: 0, fontWeight: 800 }}>Din placering: <strong>#{you.rank}</strong></p>}
        </div>
      </div>
      {me && (
        <div className={`res-podium ${you.outcome === "win" ? "win" : ""}`}>
          <PodiumStage
            equipped={me.avatar}
            progress={me.levelProgress.into / Math.max(1, me.levelProgress.needed)}
            celebrate={{ kind: ((me.avatar.emote?.data?.victory && me.avatar.emote.data.kind) || (you.outcome === "win" ? "cheer" : "wave")) as any, n: 1 }}
            label="Din figur på podiet"
            fallback={<Avatar equipped={me.avatar} size={150} framing="full" label="Din figur" />}
          />
        </div>
      )}
      <div className="big-result">
        <div className="rewards" ref={rewardsRef}>
          <div className="reward"><Coin /> +<Count to={you.coins} /><small>mønter</small></div>
          <div className="reward xp">+<Count to={you.xp} /><small>XP</small></div>
        </div>
        {you.capped && <p className="muted">Du har nået dagens grænse for spillemønter ({you.coins} af {you.coinsEarnedBeforeCap} blev gemt).</p>}
        {you.streak?.extended && <StreakNote streak={you.streak} />}
        {you.levelUp && <div className="notice good" role="status">Niveau op! Du er nu niveau {you.level}.{(() => { const t = TOWER_TYPES.find((k) => TOWERS[k].unlock === you.level); return t ? ` 🔓 Nyt forsvar i Tårnforsvar: ${TOWERS[t].icon} ${TOWERS[t].name}!` : ""; })()}</div>}
      </div>
      {(you.newMissions?.length > 0 || you.newAchievements?.length > 0 || you.newGroups?.length > 0) && (
        <div className="newwins" role="status">
          <h3 className="nw-title"><LuSparkles aria-hidden="true" size={22} /> Nyt – hent din præmie!</h3>
          <div className="nw-grid">
            {you.newGroups.map((g: any) => <div key={g.id} className="nw-card gold"><span className="nw-ico"><LuMedal aria-hidden="true" size={30} /></span><b>{g.name}</b><small>Badge klaret!</small><span className="nw-coins"><Coin /> +{g.coins} venter</span>{g.title && <small>Titel: {g.title}</small>}</div>)}
            {you.newMissions.map((m: any) => <div key={m.key} className="nw-card mission"><span className="nw-ico"><LuTarget aria-hidden="true" size={28} /></span><b>{m.title}</b><small>Mission klaret</small><span className="nw-coins"><Coin /> +{m.coins} venter</span></div>)}
            {you.newAchievements.map((a: any) => <div key={a.id} className="nw-card"><span className="nw-ico"><LuStar aria-hidden="true" size={28} /></span><b>{a.title}</b><small>Delmål nået</small><span className="nw-coins"><Coin /> +{a.coins} venter</span></div>)}
          </div>
          <div className="nw-go"><Link to="/praemier" className="btn gold"><LuGift aria-hidden="true" size={18} /> Hent præmierne under Mål</Link></div>
        </div>
      )}
      {me?.goal && earned > 0 && <GoalStrip goal={me.goal} balance={me.balance} earned={earned} />}
      <NextGoal />
      <details className="calc">
        <summary>Sådan blev belønningen regnet ud</summary>
        <table className="t"><tbody>{you.lines.map((l: any, i: number) => <tr key={i}><td>{l.label}</td><td><Coin /> {l.coins}</td><td>{l.xp} XP</td></tr>)}</tbody></table>
      </details>
      {review.length > 0 && (
        <>
          <h3>Dine svar og forklaringer</h3>
          <div className="stack">
            {review.map((r, i) => (
              <details key={i} className="card flat">
                <summary>{r.correct ? <LuCheck aria-label="Rigtigt" role="img" size={16} /> : <LuX aria-label="Forkert" role="img" size={16} />} {r.text}</summary>
                <p>Rigtigt svar: <strong>{r.correctText}</strong></p>
                <p className="muted">{r.explanation}</p>
              </details>
            ))}
          </div>
        </>
      )}
      <div className="row">
        <Link to={`/spil/${view.game}`} className="btn lime">Spil igen</Link>
        <Link to="/spil" className="btn ghost">Andre spil</Link>
        <Link to="/" className="btn ghost">Hjem</Link>
      </div>
      {st?.kind === "sim" && st.rankings?.length > 0 && (
        <div>
          <h3>Slutstilling</h3>
          <ol>{st.rankings.map((r: any) => <li key={r.seat}>{view.players.find((p: any) => p.seat === r.seat)?.username}: {r.score}{st.game === "videnraes" ? " m" : " point"}</li>)}</ol>
        </div>
      )}
    </div>
  );
}

/** Shared by the turn-based games: left = question/feedback, centre = the board, right = match status. `wide` puts the boards first and the question on the right. */
export function TurnFrame({ view, answer, busy, feedback, clearFeedback, children, statusExtra, wide }: GameProps & { children?: any; statusExtra?: any; wide?: boolean }) {
  const st = view.state;
  const mine = st.turn === st.you;
  const over = st.phase === "over";
  const opp = view.players.find((p: any) => p.seat !== view.you);
  let status = "";
  if (over) status = st.winner === "draw" ? "Uafgjort" : st.winner === st.you ? "Du vandt!" : `${view.players.find((p: any) => p.seat === st.winner)?.username ?? "Modstanderen"} vandt`;
  else if (st.phase === "setup") status = "Gør din opstilling klar";
  else if (st.phase === "question" && mine) status = view.game === "kortkamp" ? "Svar rigtigt, så slipper du for at trække kort!" : view.game === "monopoly" ? "Svar på spørgsmålet!" : view.game === "ludo" ? "Svar rigtigt – så slår du terningen!" : view.game === "yatzy" ? "Svar rigtigt – så får du alle tre slag!" : "Svar rigtigt for at få lov til at spille!";
  else if (st.phase === "action" && mine) status = view.game === "kortkamp" ? "Din tur – læg et kort!" : view.game === "ludo" ? "Din tur – vælg en brik!" : view.game === "yatzy" ? "Din tur – hold terningerne og skriv point!" : "Rigtigt! Nu er det din tur at spille.";
  else status = `${view.players.find((p: any) => p.seat === st.turn)?.username ?? opp?.username ?? "Modstanderen"} er i gang…`;
  useEffect(() => {
    if (st.question) clearFeedback();
    // eslint-disable-next-line
  }, [st.question?.pid]);
  /* the question that was just answered stays on screen with the right option lit up, until the next question arrives */
  const [recent, setRecent] = useState<{ q: QView; given: number | string } | null>(null);
  const onAnswer = (given: number | string) => { if (st.question) setRecent({ q: st.question, given }); return answer(given); };
  const me = st.stats?.[st.you];
  const waiting = !mine && !over && st.phase !== "setup";
  const notice = <div className={`notice ${mine && !over ? "good" : ""} ${waiting ? "thinking" : ""}`} role="status" aria-live="polite"><strong>{status}</strong>{statusExtra}{view.config.practice && <span className="muted"> · Øvetilstand</span>}</div>;
  const qblock = (
    <>
      {st.question ? <QuestionCard q={st.question} serverNow={st.now} onAnswer={onAnswer} busy={busy} totalMs={(st.timerSec ?? 30) * 1000} />
        : recent && feedback ? <QuestionCard q={recent.q} reveal={revealFor(recent.q, recent.given, feedback)} onAnswer={() => undefined} />
        : <div className="card"><div className="panel-title">Dit spørgsmål</div><p className="muted" style={{ margin: 0 }}>{mine && !over ? "Dit næste spørgsmål kommer, når du skal bruge et træk." : "Spørgsmål dukker op, når det er din tur."}</p></div>}
      {!st.question && feedback && !recent && <FeedbackBox fb={feedback} />}
    </>
  );
  const stats = (
    <section className="card" aria-label="Kampstatus">
      <div className="panel-title">Kampstatus</div>
      <div className="statline"><span>Rigtige svar</span><b>{me?.correct ?? 0} / {me?.answered ?? 0}</b></div>
      <div className="statline"><span>Bedste stribe</span><b>{me?.best ?? 0}</b></div>
      {opp && st.stats?.[opp.seat] && <div className="statline"><span>{opp.username}</span><b>{st.stats[opp.seat].correct ?? 0} rigtige</b></div>}
      <HowTo game={view.game} />
      <details className="rules"><summary>Regler</summary><p>{GAME_META[view.game]?.how}</p></details>
    </section>
  );
  return (
    <div className={`arena-grid ${wide ? "wide" : ""}`}>
      {!wide && <div className="sidecol l">{notice}{qblock}</div>}
      <div className="arena-center">
        {wide && notice}
        {children}
        {MOTTO[view.game] && <div className="motto">{MOTTO[view.game]}</div>}
      </div>
      <div className="sidecol r">{wide && qblock}{stats}</div>
    </div>
  );
}
